import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@hero/web-lib';
import { LockIcon } from '@hero/web-icons';
import {
  paymentMadaLogo,
  paymentVisaLogo,
  paymentMastercardLogo,
  paymentApplePayLogo,
} from '@hero/web-assets';

export interface BillingTrustStripProps {
  className?: string;
}

/**
 * Payment methods Moyasar accepts. Rendered as colored `<img>` tags so
 * each brand's official mark survives untouched. Labels are
 * intentionally plain English brand names (proper nouns, not localized).
 */
const METHODS: { name: string; src: string }[] = [
  { name: 'Mada', src: paymentMadaLogo },
  { name: 'Visa', src: paymentVisaLogo },
  { name: 'Mastercard', src: paymentMastercardLogo },
  { name: 'Apple Pay', src: paymentApplePayLogo },
];

/**
 * Trust footer for the checkout summary card. Shows the payment-method
 * marks Moyasar supports and a small lock note crediting the processor.
 */
export function BillingTrustStrip({ className }: BillingTrustStripProps) {
  const { t } = useTranslation('m-billing');
  return (
    <div className={cn('flex flex-col items-center gap-3 text-center', className)}>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {METHODS.map(method => (
          <span
            key={method.name}
            className="inline-flex h-7 items-center justify-center rounded-md border border-gray-200 bg-white px-2"
            title={method.name}>
            <img
              src={method.src}
              alt={method.name}
              className="h-4 w-auto max-w-[44px] object-contain"
              loading="lazy"
              decoding="async"
            />
          </span>
        ))}
      </div>

      <div className="flex flex-col items-center gap-1 text-[11px] text-gray-500">
        <span className="inline-flex items-center gap-1.5">
          <LockIcon className="size-3.5 text-gray-400" />
          {t('checkout.trust.lock_note')}
        </span>
        <span>{t('checkout.trust.cancel_anytime')}</span>
      </div>
    </div>
  );
}
